import { API } from 'aws-amplify';
import { useClientCode } from 'customHooks/useParameters';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ActionEnum,
  Clients,
  GetBriefTemplateQuery,
  GetConcernsQuery,
  GetPortalDashboardsQuery,
  GetProjectLevelQuery,
  GetUserV2Query,
  GetUserV2QueryVariables,
  PortalDashboardItem,
  StatementType,
  UserIsAuthorizedMultiQuery,
  briefTemplateSection,
  country,
  getUserTypeV2,
} from '../API';
import {
  getBriefTemplate,
  getConcerns,
  getPortalDashboards,
  getProjectLevel,
  getUserV2,
  userIsAuthorizedMulti,
} from '../graphql/queries';
import APIContext from './APIContext';
import AuthContext from './AuthContext';

export interface OrganizationWithCountry {
  country: {
    country: string;
    countryEnum: string;
  };
  Organization?: string | undefined;
  OrganizationId?: string | undefined;
}

export type IActiveOrganization =
  | {
      Organization: string;
      OrganizationId: string;
    }
  | undefined;

type ClientContextProps = {
  setActiveOrganization: (args: IActiveOrganization) => void;
  setCheckParams: (args: any) => void;
  activeOrganization: IActiveOrganization;
  unpublishedStatement: any[];
  checkPaddington: getUserTypeV2 | undefined;
  organizationList: IActiveOrganization[];
  setOrganizationList: (args: IActiveOrganization[]) => void;
  anyPublish: boolean | undefined;
  anyCreate: boolean;
  briefTemplate: briefTemplateSection[] | undefined;
  briefTemplateID: string;
  fetchBriefTemplate: () => void;
  setOrganizationListCountry: (args: country | undefined) => void;
  organizationListWithCountry: OrganizationWithCountry[];
  fetchMultipleAuthorizations: (action: string) => void;
  userAccess: UserAccess | null;
  typeOfReadAccess: ReadAccessType[];
  getLevel: (fullClients: any[]) => void;
  isThereAnyReadPlus: boolean | undefined;
  userCanDoInsource: boolean;
  loggedInUserAccessType: getUserTypeV2 | undefined;
};

export interface UserAccess {
  fullAccess: boolean;
  readAccess: boolean;
  noAccess: boolean;
}

export type ReadAccessType =
  | {
      ClientCode: string;
      ClientCountry: string;
      Level: string;
    }
  | [];

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

export const ClientContextProvider = (props: any) => {
  const [activeOrganization, setActiveOrganization] = useState<IActiveOrganization>();
  const [checkParams, setCheckParams] = useState();
  const { isAuthorized, user } = useContext(AuthContext);
  const [unpublishedStatement, setUnpublishedStatement] = useState<any>();
  const [checkPaddington, setCheckPaddington] = useState<getUserTypeV2>();
  const [organizationList, setOrganizationList] = useState<Array<IActiveOrganization>>([]);
  const { getEnvironment } = useContext(APIContext);
  const [anyPublish, setAnyPublish] = useState<boolean | undefined>(undefined);
  const [anyCreate, setAnyCreate] = useState<boolean>(false);
  const [userAccess, setUserAccess] = useState(null as UserAccess | null);

  // Get organization from URL parameters
  const [org] = useClientCode();

  const translateCountryList = useMemo(
    () => [
      { country: 'NO', countryEnum: 'NORWAY' },
      { country: 'DK', countryEnum: 'DENMARK' },
      { country: 'FI', countryEnum: 'FINLAND' },
      { country: 'SE', countryEnum: 'SWEDEN' },
      { country: 'NC', countryEnum: 'NORDICS' },
      { country: 'GL', countryEnum: 'GLOBAL' },
      { country: null, countryEnum: 'NA' },
    ],
    [],
  );

  // const distinctCountries = organizationList
  //   .map((e) => {
  //     const country = e?.OrganizationId.split('#')[1];
  //     const foundCountry = translateCountryList.find((e) => e.country === country);
  //     return foundCountry;
  //   })
  //   .filter((e, i, a) => a.indexOf(e) === i);

  const organizationListWithCountry: OrganizationWithCountry[] = organizationList.map((e) => {
    const country = e?.OrganizationId.split('#')[1];
    const foundCountry = translateCountryList.find((e) => e.country === country);
    if (foundCountry) {
      if (foundCountry.country === null) {
        return { ...e, country: { country: 'NA', countryEnum: 'NA' } };
      }
      return { ...e, country: foundCountry };
    }
    return { ...e, country: { country: 'NA', countryEnum: 'NA' } };
  });

  // const handleCountrySelection = (country: country) => {
  //   setOrganizationListCountry(country);
  // };

  const [organizationListCountry, setOrganizationListCountry] = useState<country | undefined>();

  useEffect(() => {
    const fetchConcernsSequentially = async () => {
      try {
        const allConcerns: IActiveOrganization[] = [];

        // Get all valid countries from translateCountryList (excluding null)
        const countriesToFetch = translateCountryList
          .filter((c) => c.country !== null)
          .map((c) => c.country as country);

        // If organizationListCountry is specified, only fetch for that country
        const targetCountries = organizationListCountry ? [organizationListCountry] : countriesToFetch;

        // Fetch concerns sequentially for each country to avoid network congestion
        for (const country of targetCountries) {
          try {
            const response = (await API.graphql({
              query: getConcerns,
              variables: {
                Environment: getEnvironment,
                Country: country,
              },
              authMode: 'AMAZON_COGNITO_USER_POOLS',
            })) as { data: GetConcernsQuery };

            const countryConcerns = response.data.getConcerns as Array<IActiveOrganization>;

            if (countryConcerns && countryConcerns.length > 0) {
              allConcerns.push(...countryConcerns);
            }

            // Add small delay between requests to prevent network congestion
            await new Promise((resolve) => setTimeout(resolve, 100));
          } catch (countryError) {
            console.log(`ERROR GET CONCERNS for country ${country}:`, countryError);
            // Continue with other countries even if one fails
          }
        }

        // Remove duplicates based on OrganizationId
        const uniqueConcerns = allConcerns.filter(
          (concern, index, self) => index === self.findIndex((c) => c?.OrganizationId === concern?.OrganizationId),
        );

        setOrganizationList(uniqueConcerns);

        // Validate organization immediately after getting concerns
        if (org && org !== 'none' && uniqueConcerns.length > 0) {
          const clientDetails = uniqueConcerns.find((e) => e?.OrganizationId === org);

          if (clientDetails === undefined) {
            alert(`You are not authorized to see this organization ${org}`);
            // Could also redirect to home page or show error message
            return;
          } else {
            // Automatically set the active organization if it's valid
            setActiveOrganization(clientDetails);
          }
        }
      } catch (e) {
        console.log('ERROR IN SEQUENTIAL FETCH CONCERNS:', e);
      }
    };

    if (isAuthorized) {
      fetchConcernsSequentially();
    }
  }, [isAuthorized, organizationListCountry, getEnvironment, translateCountryList, org]);

  const [typeOfReadAccess, setTypeOfReadAccess] = useState<ReadAccessType[]>([]);

  const getLevel = async (fullClients: any[]) => {
    fullClients.forEach((e) => {
      delete e?.__typename;
    });
    try {
      const response = (await API.graphql({
        query: getProjectLevel,
        variables: {
          Clients: fullClients,
          Environment: getEnvironment,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetProjectLevelQuery };
      // console.log(response.data.getProjectLevel, "GET LEVEL")
      const responseArray = response.data.getProjectLevel;
      // console.log("typeOfReadAccess", responseArray)
      const enrichedResponse = fullClients.map((e) => {
        const found = responseArray?.find((r) => r?.ClientCode === e.ClientCode);
        if (found) {
          return { ...e, Level: 'READ_PLUS_ACCESS' };
        }
        return { ...e, Level: 'READ_ACCESS' };
      });
      setTypeOfReadAccess(enrichedResponse as ReadAccessType[]);
    } catch (error) {
      console.error('Error in getLevel', error);
    }
  };

  // GET USER TYPE
  const fetchUserPaddington = useCallback(async () => {
    setCheckPaddington(undefined);
    try {
      const response = (await API.graphql({
        query: getUserV2,
        variables: {
          Environment: getEnvironment, // Environment.DEMO,
          Organization_id: activeOrganization?.OrganizationId,
          StatementType: StatementType.Paddington,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetUserV2Query };

      const fullAccess = response.data.getUserV2?.Access?.filter((e) => e?.Level?.includes('FULL'))[0]?.Clients;
      const readAccess = response.data.getUserV2?.Access?.filter((e) => e?.Level?.includes('READ'))[0]?.Clients;
      const noAccess = response.data.getUserV2?.Access?.filter((e) => e?.Level?.includes('NO'))[0]?.Clients;

      if (fullAccess) {
        getLevel(fullAccess as Clients[]);
      }
      const user = {
        fullAccess: fullAccess ? fullAccess.length > 0 : false,
        readAccess: readAccess ? readAccess.length > 0 : false,
        noAccess: noAccess ? noAccess.length > 0 : false,
      };
      setUserAccess(user);
      if ((fullAccess && fullAccess.length > 0) || (readAccess && readAccess.length > 0)) {
        setCheckPaddington(response.data.getUserV2 as getUserTypeV2);
      }
    } catch (e) {
      console.log('ERROR IN FETCH DASHBOARDS', e);
    }
  }, [activeOrganization, getEnvironment]);

  const [isThereAnyReadPlus, setIsThereAnyReadPlus] = useState<boolean | undefined>(undefined);

  const fetchMultipleAuthorizations = useCallback(
    async (action: string) => {
      try {
        const response = (await API.graphql({
          query: userIsAuthorizedMulti,
          variables: {
            Organization_id: activeOrganization?.OrganizationId,
            Environment: getEnvironment,
            Resource: 'Queries', // Write publish instead of queries,
            Service: 'OMD Portal',
            StatementType: StatementType.Access,
            Action: ActionEnum[action as keyof typeof ActionEnum],
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: UserIsAuthorizedMultiQuery };
        const data = response.data.userIsAuthorizedMulti;
        if (data && data.length > 0 && action === 'PUBLISH') {
          // console.log("DATA", data)
          setAnyPublish(data.some((e) => e?.Authorized === true));
          const isAllUnauthorized = data.every((e) => e?.Authorized === false);
          if (isAllUnauthorized) {
            try {
              const response = (await API.graphql({
                query: getUserV2,
                variables: {
                  Environment: getEnvironment, // Environment.DEMO,
                  Organization_id: activeOrganization?.OrganizationId,
                  StatementType: StatementType.Access,
                },
                authMode: 'AMAZON_COGNITO_USER_POOLS',
              })) as { data: GetUserV2Query };
              const anyReadPlusClients = response.data.getUserV2?.Access?.find((e) => e?.Level === 'READ_PLUS_ACCESS');
              if (anyReadPlusClients?.Clients) {
                if (anyReadPlusClients.Clients.length > 0) {
                  setIsThereAnyReadPlus(true);
                } else {
                  setIsThereAnyReadPlus(false);
                }
              }
            } catch (e) {
              console.log('ERROR IN FETCH DASHBOARDS', e);
            }
          }
        } else if (data && data.length > 0 && action === 'CREATE') {
          setAnyCreate(data.some((e) => e?.Authorized === true));
        }

        return data;
      } catch (e) {
        console.log('ERROR IN FETCH USER IS AUTHORIZED MULTI', e);
      }
    },
    [activeOrganization, getEnvironment],
  );

  const [briefTemplate, setBriefTemplate] = useState<briefTemplateSection[] | undefined>([]);
  const [briefTemplateID, setBriefTemplateID] = useState<string>('');

  const fetchBriefTemplate = async () => {
    try {
      const response = (await API.graphql({
        query: getBriefTemplate,
        variables: {
          Environment: getEnvironment,
          OrganizationId: activeOrganization?.OrganizationId,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetBriefTemplateQuery };

      const res = response.data.getBriefTemplate?.sections as briefTemplateSection[];
      // inn all items in the res array sorty columns by placeholder property
      res.forEach((e) => {
        e.columns?.sort((a, b) => {
          if (parseInt(a?.placeholder as string, 10) > parseInt(b?.placeholder as string, 10)) {
            return 1;
          } else {
            return -1;
          }
        });
      });
      setBriefTemplate(response.data.getBriefTemplate?.sections as briefTemplateSection[]);
      setBriefTemplateID(response.data.getBriefTemplate?.templateId as string);
    } catch (error) {
      console.log('Error fetching the brief template', error);
    }
  };

  const [loggedInUserAccessType, setLoggedInUserAccessType] = useState<getUserTypeV2>();
  const fetchAccess = async () => {
    try {
      const response = (await API.graphql({
        query: getUserV2,
        variables: {
          Organization_id: activeOrganization?.OrganizationId,
          Environment: getEnvironment,
          StatementType: StatementType.Access,
        } as GetUserV2QueryVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetUserV2Query };
      console.log('LOGGED USER HAS THIS ACCESS', response);
      const data = response.data.getUserV2;
      if (data) {
        setLoggedInUserAccessType(data);
      } else {
        setLoggedInUserAccessType(undefined);
      }
    } catch (error) {
      console.error('Error fetching userIsAuthorized', error);
    }
  };

  const [userCanDoInsource, setUserCanDoInsource] = useState<boolean>(false);
  const fetchIsInsource = async () => {
    try {
      const response = (await API.graphql({
        query: userIsAuthorizedMulti,
        variables: {
          Organization_id: activeOrganization?.OrganizationId,
          Environment: getEnvironment,
          Resource: 'INSOURCE', // Write publish instead of queries,
          Service: 'OMDPortal',
          StatementType: StatementType.Access,
          Action: ActionEnum.WRITE,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UserIsAuthorizedMultiQuery };
      const data = response.data.userIsAuthorizedMulti;
      if (data) {
        const isIt = data.some((e) => e?.Authorized === true);
        console.log('INSOURCE', data, isIt);
        setUserCanDoInsource(isIt);
      } else {
        setUserCanDoInsource(false);
      }

      // console.log("INSOURCE", data)
    } catch (e) {
      console.log('ERROR IN FETCH USER IS AUTHORIZED MULTI', e);
    }
  };

  useEffect(() => {
    if (activeOrganization !== undefined) {
      fetchIsInsource();
      fetchUserPaddington();
      fetchMultipleAuthorizations('PUBLISH');
      fetchMultipleAuthorizations('CREATE');
      fetchBriefTemplate();
      fetchAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization, fetchUserPaddington]);

  return (
    <ClientContext.Provider
      value={{
        setActiveOrganization,
        setCheckParams,
        activeOrganization,
        unpublishedStatement,

        checkPaddington,
        organizationList,
        setOrganizationList,
        anyPublish,
        anyCreate,
        briefTemplate,
        briefTemplateID,
        fetchBriefTemplate,
        setOrganizationListCountry,
        organizationListWithCountry,
        fetchMultipleAuthorizations,
        userAccess,
        typeOfReadAccess,
        getLevel,
        isThereAnyReadPlus,
        userCanDoInsource,
        loggedInUserAccessType,
      }}
    >
      {props.children}
    </ClientContext.Provider>
  );
};

export default ClientContext;
//http://localhost:3000/onlinePortal?client=Telenor&campaignID=23188

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('context needs to be provided in client context');
  }
  return context;
};
